import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { Tabs } from "antd";
import { Hooks, Context, Util } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { XtAuth, XtContext } = Context;
import styles from "./index.module.scss";
import cx from "classnames";
import LayoutXt from "components/Layout/xt";
/**预览 */
import UserOverview from "components/Pages/User/Overview";
/**安全中心 */
import UserIndex from "components/Pages/User/Index";
import UserEmail from "components/Pages/User/Email";
import UserMobil from "components/Pages/User/Mobile";
/**实名认证 */
import UserVerification from "components/Pages/User/Verification";
import UserLevels from "components/Pages/User/Levels";
import UserInstitutions from "components/Pages/User/Institutions";
/**防钓鱼码 */
import UserAntiPhising from "components/Pages/User/Anti-Phising";
import UserLoginPassword from "components/Pages/User/Login-Password";
import UserResetVerification from "components/Pages/User/Reset-Verification";
import UserGoogleAuthenticator from "components/Pages/User/Google-Authenticator";
/**api 管理 */
import UserApi from "components/Pages/User/Api";
import PaymentMethod from "components/Pages/User/PaymentMethod";
import UserAbout from "components/Pages/User/About";

import store from "store";
import { GetStaticPaths, GetStaticPropsContext } from "next";
import Setting from "@/components/Pages/User/Setting";
import SettingLanguage from "@/components/Pages/User/Setting/SettingLanguage";
import SettingCurrency from "@/components/Pages/User/Setting/SettingCurrency";
import NotificationSetting from "@/components/Pages/User/Setting/NotificationSetting";
import getCommonStaticProps from "@xt/ssg";
import { getAbsoluteUrl, getZendeskHcLocale } from "utils/method";
import { openLivehelp100Support } from "@/utils/livehelp100";

const securityInnerKeys = [
  "email", // 安全中心-绑定邮箱
  "google-authenticator", // 安全中心-更改谷歌验证器
  "mobile", // 安全中心-更改手机
  "login-password", // 安全中心-更改密码
  "anti-phishing", // 安全中心-设置防钓鱼码
  "security-password", // 安全中心-更改安全密码
  "reset-password", // 安全中心-重置安全密码
  "reset-verification", // 安全中心-重置安全验证
];
const verifyInnerKeys = [
  "levels", // 实名认证-初高级认证
  "institutions", // 实名认证-企业认证
];
const TAB_KEYS = [
  "overview", //预览主页
  "security-center", // 安全中心主页
  ...securityInnerKeys,
  "verification", // 实名认证主页
  ...verifyInnerKeys,
  "api", // api管理主页
  "payment-method", // 支付方式列表
  "setting",
  "setting-language", // 偏好设置-语言
  "setting-currency", // 偏好设置-计价方式
  "notification-setting", // 通知设置
  "about", // 关于
];
const mapKey = (tabKey: string) => {
  // 不在列表中的返回 'index'
  if (!TAB_KEYS.includes(tabKey)) return "index";
  // 返回安全中心
  if (securityInnerKeys.includes(tabKey)) return "security-center";
  // 初高级认证，企业认证返回实名认证tab
  if (verifyInnerKeys.includes(tabKey)) return "verification";
  return tabKey;
};
/* eslint-disable */
const PageAccount: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { query, push, locale: routerLocale } = useRouter();
  const tabKey = query.tabKey as string;
  const [, appDispatch] = React.useContext(XtContext);
  const [activeKey, setActiveKey] = useState(mapKey(tabKey));
  /**useEffect */
  useEffect(() => {
    appDispatch({
      payload: {
        theme: "dark",
      },
    });
    document.body.setAttribute("data-theme", "dark");
    store.currency.getCurrencies();
  }, []);

  useEffect(() => {
    if (!store.app.clientSideReady) return;
    store.currency.loopGetPriceCurrencyConvert(store.balances.convertCurrency);
  }, [store.balances.convertCurrency, store.app.clientSideReady]);

  useEffect(() => {
    setActiveKey(mapKey(tabKey));
  }, [tabKey]);

  /**useMemo */
  const getVerifyComp = useMemo(() => {
    const mapComp = {
      verification: <UserVerification />,
      levels: <UserLevels />,
      institutions: <UserInstitutions />,
    };
    return mapComp[tabKey];
  }, [tabKey]);

  const getSecurityComp = useMemo(() => {
    const mapComp = {
      "security-center": <UserIndex />,
      email: <UserEmail />,
      "google-authenticator": <UserGoogleAuthenticator />,
      mobile: <UserMobil />,
      "login-password": <UserLoginPassword />,
      "anti-phishing": <UserAntiPhising />,
      "security-password": <UserIndex />,
      "reset-password": <UserIndex />,
      "reset-verification": <UserResetVerification />,
    };
    return mapComp[tabKey];
  }, [tabKey]);

  const noTabBarCs = useMemo(() => {
    if (securityInnerKeys.includes(tabKey)) return "no-tabs-bar";
    return "";
  }, [tabKey]);
  /**method */
  const tabHandleClick = (key: string) => {
    activeKey !== key && setActiveKey(key);
    push(`/accounts/user/${key}`, `/accounts/user/${key}`, {
      shallow: true,
    });
  };

  const getHomeUrl = () =>
    getAbsoluteUrl({ pathname: "/", locale: routerLocale || "en", basePath: "" });

  const handleBack = () => {
    // overview 页返回固定到首页（携带当前多语言）
    if (tabKey === "overview") {
      window.location.href = getHomeUrl();
      return;
    }
    // 设置语言页、设置页返回 overview（选择语言后跳到设置页，再点返回应回 overview）
    if (tabKey === "setting-language" || tabKey === "setting") {
      push("/accounts/user/overview", "/accounts/user/overview", { shallow: true });
      return;
    }
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = getHomeUrl();
    }
  };

  const handleGoHome = () => {
    window.location.href = getHomeUrl();
  };

  return (
    <LayoutXt isShowNav={false} isShowFoot={false}>
      <div className={styles.backHeader}>
        <div className={styles.headerBar}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={handleBack} type="button" aria-label={t("app.back") || "Back"}>
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="16" viewBox="0 0 11 16" fill="none">
                <path d="M0 8L8.53333 0L10.6667 2L4.26667 8L10.6667 14L8.53333 16L0 8Z" fill="white"/>
              </svg>
            </button>
            {tabKey !== "overview" ? (
              <button className={styles.homeButton} onClick={handleGoHome} type="button" aria-label={t("header.home") || t("header.derivativesOverview2") || "Home"}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </button>
            ) : null}
          </div>
          {/* 设计稿：右侧客服 + 帮助 */}
          <div className={styles.headerRight}>
            <button
              className={styles.iconButton}
              type="button"
              aria-label={t("account.onlineService")}
              onClick={openLivehelp100Support}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  fill="#fff"
                  d="M10 0c4.456 0 8.161 3.207 8.939 7.438A1.82 1.82 0 0 1 20 9.091v3.637a1.82 1.82 0 0 1-1.09 1.666c-1 3.13-3.556 4.934-6.322 5.45a.5.5 0 0 1-.36.156H8.682a.5.5 0 0 1-.5-.5v-1.727a.5.5 0 0 1 .5-.5h3.545a.5.5 0 0 1 .5.5v.182c2.104-.537 3.904-2.028 4.546-4.438v-.29a.5.5 0 0 1 .5-.5h.409V9.092h-.91l-.01-.374a7.272 7.272 0 0 0-14.525 0l-.01.374h-.909v3.637h.41a.5.5 0 0 1 .5.5v.818a.5.5 0 0 1-.5.5h-.41A1.82 1.82 0 0 1 0 12.727V9.092a1.82 1.82 0 0 1 1.06-1.653C1.839 3.207 5.545 0 10 0M7.682 7.272a.5.5 0 0 1 .5.5v2.637a.5.5 0 0 1-.5.5h-.819a.5.5 0 0 1-.5-.5V7.772a.5.5 0 0 1 .5-.5zm5.455 0a.5.5 0 0 1 .5.5v2.637a.5.5 0 0 1-.5.5h-.819a.5.5 0 0 1-.5-.5V7.772a.5.5 0 0 1 .5-.5z"
                />
              </svg>
            </button>
            <button
              className={styles.iconButton}
              type="button"
              aria-label={t("account.helpCenter")}
              onClick={() => {
                const hc = getZendeskHcLocale(routerLocale);
                window.open(`https://spikex.zendesk.com/hc/${hc}`, "_blank", "noopener,noreferrer");
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                <path
                  fill="#fff"
                  d="M11.154 12.157c.504 0 .912.412.912.921a.917.917 0 0 1-.912.922.917.917 0 0 1-.912-.922c0-.509.409-.921.912-.921m.005-7.094c1.486 0 2.658 1.272 2.658 2.658 0 1.16-.857 2.151-1.833 2.508v.7a.826.826 0 0 1-1.65 0v-.84c0-.784.62-1.245 1.074-1.405.438-.154.758-.602.759-.963 0-.512-.47-1.008-1.008-1.008-.19 0-.45.104-.677.331s-.331.487-.331.677a.826.826 0 0 1-1.651 0c0-.726.355-1.384.815-1.844s1.117-.814 1.844-.814"
                />
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M18.394 1.65c.76 0 1.375.564 1.375 1.26v16.18c0 .696-.616 1.26-1.375 1.26H5.58c-2.115 0-3.83-1.57-3.83-3.506V5.156c0-1.936 1.715-3.506 3.83-3.506zM5.784 16.958c-.779 0-1.45.463-1.752 1.128.378.395.93.645 1.548.645H18v-1.773zM5.58 3.27c-1.138 0-2.061.845-2.061 1.887V16.12a3.56 3.56 0 0 1 2.265-.812H18V3.269z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      {tabKey === "reset-verification" ? (
        <div className={cx(styles.container, styles["account_container"], styles["no-tabs-bar"])}>
          <UserResetVerification />
        </div>
      ) : tabKey === "payment-method" ? (
        <div className={cx(styles.container, styles["account_container"])}>
          <PaymentMethod />
        </div>
      ) : tabKey === "overview" ? (
        <div className={cx(styles.container, styles["account_container"])}>
          <UserOverview />
        </div>
      ) : (
        <XtAuth
          renderLogin={() => {
            if (tabKey === "security-center" || securityInnerKeys.includes(tabKey)) {
              return (
                <div className={cx(styles.container, styles["account_container"], styles[noTabBarCs])}>
                  {getSecurityComp}
                </div>
              );
            }
            if (tabKey === "verification" || verifyInnerKeys.includes(tabKey)) {
              return (
                <div className={cx(styles.container, styles["account_container"], styles[noTabBarCs])}>
                  {getVerifyComp}
                </div>
              );
            }
            if (tabKey === "api") {
              return (
                <div className={cx(styles.container, styles["account_container"])}>
                  <UserApi />
                </div>
              );
            }
            if (tabKey === "setting") {
              return (
                <div className={cx(styles.container, styles["account_container"])}>
                  <Setting />
                </div>
              );
            }
            if (tabKey === "setting-language") {
              return (
                <div className={cx(styles.container, styles["account_container"])}>
                  <SettingLanguage />
                </div>
              );
            }
            if (tabKey === "setting-currency") {
              return (
                <div className={cx(styles.container, styles["account_container"])}>
                  <SettingCurrency />
                </div>
              );
            }
            if (tabKey === "notification-setting") {
              return (
                <div className={cx(styles.container, styles["account_container"])}>
                  <NotificationSetting />
                </div>
              );
            }
            if (tabKey === "about") {
              return (
                <div className={cx(styles.container, styles["account_container"])}>
                  <UserAbout />
                </div>
              );
            }
            return (
              <div className={cx(styles.container, styles["account_container"])}>
                <UserOverview />
              </div>
            );
          }}
          onAuthError={() => {
            window.location.href = Util.getUrl("/accounts/login?backUrl=" + encodeURIComponent(location.href));
          }}
        ></XtAuth>
      )}
    </LayoutXt>
  );
};
export default PageAccount;

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function getStaticProps(context: GetStaticPropsContext) {
  const { params, locale } = context;
  const localePath = !locale || locale == "default" ? "" : "/" + locale;
  if (!TAB_KEYS.includes((params!.tabKey as string) || "")) {
    return {
      redirect: {
        destination: localePath + "/accounts/user/security-center",
        permanent: false,
      },
    };
  }
  return getCommonStaticProps(context, "/accounts/user/" + params!.tabKey, { theme: "dark" });
}
